import { useEffect, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Subscribe to real-time SSE events from the backend.
 * Auto-reconnects on disconnect. Pauses when tab is hidden.
 *
 * @param {(type: string, payload: object) => void} onEvent — called for every event
 * @param {boolean} enabled — set false to disable (e.g. while logged out)
 */
export default function useEventStream(onEvent, enabled = true) {
  const esRef = useRef(null);
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return undefined;
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    let stopped = false;
    let reconnectTimer = null;

    const connect = () => {
      if (stopped) return;
      try {
        const url = `${BACKEND_URL}/api/events/stream?token=${encodeURIComponent(token)}`;
        const es = new EventSource(url);
        esRef.current = es;

        es.addEventListener('message', (ev) => {
          try {
            const { type, payload } = JSON.parse(ev.data);
            handlerRef.current?.(type, payload || {});
          } catch { /* ignore bad frames */ }
        });

        es.addEventListener('error', () => {
          es.close();
          esRef.current = null;
          if (stopped) return;
          // Reconnect with backoff
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(connect, 3000);
        });
      } catch {
        if (!stopped) {
          reconnectTimer = setTimeout(connect, 3000);
        }
      }
    };

    connect();

    // Pause on tab hidden, resume on visible
    const onVisibilityChange = () => {
      if (document.hidden) {
        if (esRef.current) {
          esRef.current.close();
          esRef.current = null;
        }
      } else if (!esRef.current) {
        connect();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled]);
}
